import type { SheetDrawnTopo, SheetPicture, SheetTopo } from './sheet-data';

/**
 * The sectors' photos as JPEG data URLs, which react-pdf can draw whatever the
 * file was: it reads neither WebP nor HEIC, and a screenshot of a cliff can be a
 * 13 MB PNG that would go into every PDF whole. Fetched rather than drawn from
 * the address, because a canvas that drew a picture from another origin can no
 * longer be read back.
 *
 * A photo that cannot be read leaves the sheet without it, and the page says so:
 * the list is what the sheet is for.
 */

/** About 270 dpi across the width of an A4 page, which is more than an office printer shows. */
const PHOTO_LONG_EDGE = 2000;
const PHOTO_QUALITY = 0.85;
const PHOTO_TIMEOUT_MS = 30_000;

function drawToJpeg(src: string): Promise<SheetPicture | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const scale = Math.min(1, PHOTO_LONG_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
        const width = Math.max(1, Math.round(img.naturalWidth * scale));
        const height = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return resolve(null);
        // JPEG has no transparency: a see-through corner would come out black.
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, width, height);
        context.drawImage(img, 0, 0, width, height);
        resolve({ src: canvas.toDataURL('image/jpeg', PHOTO_QUALITY), width, height });
      } catch {
        resolve(null);
      }
    };
    img.src = src;
  });
}

async function readPhoto(url: string): Promise<SheetPicture | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), PHOTO_TIMEOUT_MS);
  let objectUrl: string | null = null;
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
    if (!response.ok) return null;
    objectUrl = URL.createObjectURL(await response.blob());
    return await drawToJpeg(objectUrl);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/**
 * The sheet is made again at every change of its settings, and only the first
 * time should wait for the photos. A failure is forgotten, so "try again" tries.
 */
const photos = new Map<string, Promise<SheetPicture | null>>();

function loadPhoto(url: string): Promise<SheetPicture | null> {
  const known = photos.get(url);
  if (known) return known;
  const loading = readPhoto(url).then((picture) => {
    if (!picture) photos.delete(url);
    return picture;
  });
  photos.set(url, loading);
  return loading;
}

export async function loadSheetTopos(topos: SheetTopo[]): Promise<{ topos: SheetDrawnTopo[]; failed: boolean }> {
  const pictures = await Promise.all(topos.map((topo) => loadPhoto(topo.imageUrl)));
  const drawn = topos.flatMap(({ sectorId, sectorName, lines }, index) => {
    const picture = pictures[index];
    return picture ? [{ sectorId, sectorName, lines, picture }] : [];
  });
  return { topos: drawn, failed: drawn.length < topos.length };
}
